import React, { useCallback, useEffect, useMemo, useState } from 'react';
import 'owl.carousel/dist/assets/owl.carousel.css';
import 'owl.carousel/dist/assets/owl.theme.default.css';
import { useNavigate } from 'react-router-dom';
import HistoryDetail from './HistoryDetail';
import HistoryList from './HistoryList';
import { ById, axiosClient } from '../../../../../utils/axiosClient';
import { usePushEvents } from '../../../../../hooks/PushEvents';
import { historyShown } from '../../../../../hooks/PushEvents/clevertapEvents';
import { webConfig } from '../../../../../utils/webConfig';
import GetAppModal, { isMobile } from '../../../../GetAppModal';
import { useDonorProfile } from '../../../../../api';
import { ROUTES } from '../../../../../utils/routes';
import { HeartLoading } from '../../../../HeartLoading';
import ForceAppDownload from './ForceAppDownload';
import { FeatureFlag } from '../../../../../constants';
import { useFeatureFlag } from '../../../../../providers';
import {
    ApiHandler,
    GiftHistoryRequest,
    GiftHistoryResponse,
    useGiftSummary,
    useGiftSummaryFailedOnMount,
} from '../../../../../api';

const MAX_GIFT_COUNT = 3;
const countPerPage = 50;
const calendarYear = new Date().getFullYear();

export default function History() {
    const { isFeatureEnabled } = useFeatureFlag();
    const [open, setOpen] = useState<boolean>(true);
    const { pushEvent } = usePushEvents();
    const [historyLoading, setHistoryLoading] = useState(false);
    const retryFailedGiftSummary = useGiftSummaryFailedOnMount();
    const {
        giftSummary: gift,
        isLoading: giftLoading,
        hasNeverFetched: giftHasNeverFetched,
        isError: giftError,
    } = useGiftSummary(retryFailedGiftSummary);
    const [yearArr, setYearArr] = useState<number[]>([]);
    const [yearMetas, setYearMetas] = useState<
        ById<GiftHistoryResponse['meta']>
    >({});
    const [historyItems, setHistoryItems] = useState<any>([]);
    const [currentYear, setCurrentYear] = useState<number>(calendarYear);
    const [exportPopup, setExportPopup] = useState(false);
    const [total, setTotal] = useState<ById<string> | undefined>();
    const [exporter, setExporter] = useState(false);
    const [exportError, setExportError] = useState<string | undefined>('');
    const [historyDetailInfo, setHistoryDetailInfo] = useState<
        | (GiftHistoryResponse['data'][number] & { detailType: 'history' })
        | undefined
    >(undefined);
    const [serverError, setServerError] = useState('');
    const [startPosition, setStartPosition] = useState(0);
    const [success, setSuccess] = useState<string | undefined>();

    const { donorProfile, hasNeverFetched, isLoading, isError } =
        useDonorProfile();
    const { donor } = donorProfile || {};
    const push = useNavigate();

    const isLoadMore = useMemo(() => {
        if (yearMetas) {
            const metaInfo = yearMetas[currentYear];
            if (metaInfo) {
                return metaInfo.currentPage < metaInfo.lastPage;
            }
        }
        return false;
    }, [yearMetas, currentYear]);

    useEffect(() => {
        if (isError) {
            push(ROUTES.login);
        }
        // run only once on the error state change to avoid pushing event multiple times on re-render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isError]);

    useEffect(() => {
        pushEvent(historyShown);
        // run only once on component mount to avoid pushing event multiple times on re-render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getHistory = useCallback(
        async (data: GiftHistoryRequest) => {
            setHistoryLoading(true);
            const request =
                await ApiHandler.instance.donor.getGiftHistory(data);
            if (!request.success) {
                setServerError(request.error.message);
                setHistoryLoading(false);
                return;
            }
            const payload = request.data.response.payload;
            const yearList = payload.meta.giftsYears;
            if (!yearList.includes(currentYear)) {
                yearList.unshift(currentYear);
            }
            const metaInfos: ById<GiftHistoryResponse['meta']> = {};
            yearList.forEach((year) => {
                metaInfos[year] = undefined;
            });
            metaInfos[currentYear] = payload.meta;
            const isLoadMore = !!data.page && Number(data.page) > 1;
            setYearArr(yearList);
            // giftsTotal is the annual total for the year, not a per-page
            // value, so it shouldn't be re-derived from a "load more" page
            setTotal((prevTotal) => {
                const totalInfos: ById<string> = { ...prevTotal };
                yearList.forEach((year) => {
                    if (totalInfos[year] === undefined) {
                        totalInfos[year] = '0';
                    }
                });
                if (!isLoadMore) {
                    totalInfos[currentYear] = parseFloat(
                        payload.meta.giftsTotal.toString(),
                    )
                        .toFixed(2)
                        .replace('.00', '');
                }
                return totalInfos;
            });
            setYearMetas(metaInfos);
            setHistoryItems((prevItems: any) => {
                const items: ById<GiftHistoryResponse['data']> = {
                    ...prevItems,
                };
                yearList.forEach((year) => {
                    if (!items[year]) {
                        items[year] = [];
                    }
                });
                items[currentYear] = isLoadMore
                    ? [...(prevItems[currentYear] || []), ...payload.data]
                    : [...payload.data];
                return items;
            });
            setExporter(true);
            setHistoryLoading(false);
        },
        [currentYear],
    );

    const getYearItems = useCallback(
        (pageNum: number) => {
            const data = {
                year: currentYear,
                page: pageNum,
                perPage: countPerPage,
            };
            getHistory(data);
        },
        [currentYear, getHistory],
    );

    const loadMoreItems = useCallback(() => {
        const metaInfo = yearMetas[currentYear];
        if (metaInfo && metaInfo.currentPage < metaInfo.lastPage) {
            getYearItems(metaInfo.currentPage + 1);
        }
    }, [currentYear, getYearItems, yearMetas]);

    const closeMessage = () => () => {
        setExportError(undefined);
        setSuccess('');
    };

    const exitDetail = () => {
        setHistoryDetailInfo(undefined);
    };

    const handleHistoryDetailInfo = (
        data: GiftHistoryResponse['data'][number],
    ) => {
        setHistoryDetailInfo({
            ...data,
            detailType: 'history',
        });
    };

    const changeType = (type: number) => {
        setCurrentYear(type);
    };

    const dateFormat = (value: string) => {
        const timestamp = value.split(' ');
        const month = timestamp['0'];
        const date = timestamp['1'];
        const year = timestamp['2'];
        return `${month} ${date}, ${year}`;
    };

    const exportData = () => {
        setExportError(undefined);
        if (!donor) return;
        const data = {
            donor_id: donor.id,
            email: donor.email,
            year: currentYear,
        };

        const headers = {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        };

        axiosClient
            .post(
                webConfig.baseAPIUrl +
                    `/donor/profile/gifts-export?year=${currentYear}`,
                data,
                headers,
            )
            .then((response: any) => {
                if (response.status === 200) {
                    setSuccess(response.data.response.payload.message);
                    setExportPopup(false);
                } else {
                    setExportError(response.data.Response.Error.message);
                }
            })
            .catch((e) => {
                setExportError(
                    e.response?.data?.response?.errorMessage ||
                        'We are unable to email you at the moment, please try again!',
                );
            });
    };

    const exportPopupModalClose = () => {
        setExportPopup(false);
    };

    const exportPopupModalOpen = () => {
        setExportPopup(true);
    };

    const handleOnChanged = (value: any) => {
        if (typeof value.item !== 'undefined') {
            if (typeof value.item.index !== 'undefined') {
                if (
                    value.item.index !== null &&
                    startPosition !== value.item.index
                ) {
                    setStartPosition(value.item.index);
                }
            }
        }
    };

    useEffect(() => {
        getHistory({ year: calendarYear });
        // load on first run
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!currentYear) {
            return;
        }
        if (!historyItems || !historyItems[currentYear]?.length) {
            getYearItems(1);
        } else {
            const newYearArr = [...yearArr];
            setYearArr(newYearArr);
        }
        // listen to currentYear changes to load data for the year if not loaded yet, or just update the yearArr to trigger re-render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentYear]);

    if (giftLoading || giftHasNeverFetched || hasNeverFetched || isLoading) {
        return <HeartLoading maxWidth={300} />;
    }

    if (
        isMobile &&
        isFeatureEnabled(FeatureFlag.HIDE_GIVING_HISTORY) &&
        !giftError
    ) {
        const giftCount = gift?.all.count;
        if (giftCount && giftCount < MAX_GIFT_COUNT) {
            return <ForceAppDownload giftCount={giftCount} />;
        }
    }

    if (historyDetailInfo) {
        return (
            <HistoryDetail
                details={historyDetailInfo}
                exitDetail={exitDetail}
            />
        );
    }

    return (
        <>
            <GetAppModal
                open={open}
                setOpen={setOpen}
            />
            <HistoryList
                email={donor?.email}
                exporter={exporter}
                exportError={exportError}
                exportPopup={exportPopup}
                exportPopupModalOpen={exportPopupModalOpen}
                exportPopupModalClose={exportPopupModalClose}
                exportData={exportData}
                currentYear={currentYear}
                closeMessage={closeMessage}
                success={success}
                serverError={serverError}
                setServerError={setServerError}
                handleOnChanged={handleOnChanged}
                startPosition={startPosition}
                changeType={changeType}
                loader={historyLoading}
                historyItems={historyItems}
                total={total}
                error={''}
                handleHistoryDetailInfo={handleHistoryDetailInfo}
                dateFormat={dateFormat}
                yearArr={yearArr}
                loadMoreItems={loadMoreItems}
                isLoadMore={isLoadMore}
            />
        </>
    );
}
